import { useMemo, useState, useContext, useEffect } from 'react';
import moment from 'moment';
import {
  Select,
  Chip,
  Box,
  Stack,
  Typography,
  Paper,
  Divider,
  IconButton,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { Modal } from '@fortress-technology-solutions/fortress-component-library/Molecules';
import {
  CalendarIcon,
  AssignmentIcon,
  ArrowBackIcon,
  AlertInfoIcon,
} from '@fortress-technology-solutions/fortress-component-library/Icons';
import LimitInput from './LimitInput';
import { PageContext } from '../context';
import Spacer from '../../../components/Spacer';
import IconLink from '../../../components/IconLink';
import messages from '../messages';

export type SelectedMonthInfo = {
  currentLimit: number,
  leaseLimit: number,
  leaseCount: number,
  actualLimit: number,
  leaseDetails?: {
    scheduledLeases: { id: string, headOfHousehold: string }[],
    currentLeases: { id: string, headOfHousehold: string }[],
    priorLeases: { id: string, headOfHousehold: string }[],
  },
};

type Props = {
  monthOptions: { value: string, text: string }[],
  selectedLimitMonth: string,
  onSelectedLimitMonthChange: Function,
  limits: number[],
  leaseCounts: number[],
  selectedYears: number[],
  unitCount: number,
  isSaving: boolean,
  onSave: (year: number, month: number, value: number) => void,
  labels: [string, number][],
  monthData?: Object,
};

function SelectedLimit({
  limits,
  leaseCounts,
  selectedYears,
  unitCount,
  onSave,
  isSaving,
  monthOptions,
  selectedLimitMonth,
  onSelectedLimitMonthChange,
  labels,
  monthData,
}: Props) {
  const { canEdit, formatMessage } = useContext(PageContext);
  const [inputValue, setInputValue] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const selectedMonthInfo: SelectedMonthInfo = useMemo(() => {
    const monthsShort = moment.monthsShort();

    const dataIndex = labels.findIndex(([month, year]) => {
      const monthIndex = monthsShort.findIndex((m) => m === month);

      return `${monthIndex}-${year}` === selectedLimitMonth;
    });

    if (dataIndex < 0)
      return {
        currentLimit: 0,
        actualLimit: 0,
        leaseCount: 0,
        leaseLimit: 0,
        leaseDetails: undefined,
      };

    const [selectedMonth, selectedYear] = selectedLimitMonth.split('-');
    const leaseDetails =
      monthData?.[selectedYear]?.[selectedMonth]?.leaseDetails;

    return {
      currentLimit: limits[dataIndex],
      actualLimit: (leaseCounts[dataIndex] / unitCount) * 100,
      leaseCount: leaseCounts[dataIndex],
      leaseLimit: Math.ceil((inputValue * unitCount) / 100),
      leaseDetails,
    };
  }, [
    selectedLimitMonth,
    limits,
    leaseCounts,
    inputValue,
    labels,
    unitCount,
    monthData,
  ]);

  const isSelectedMonthInThePast = useMemo(() => {
    const [month, year] = selectedLimitMonth.split('-');
    const selectedDate = moment([year, month]);
    const today = moment();

    return selectedDate.isBefore(today, 'months');
  }, [selectedLimitMonth]);

  const areButtonsDisabled = useMemo(
    () =>
      isSaving ||
      inputValue === selectedMonthInfo.currentLimit ||
      isSelectedMonthInThePast,
    [isSaving, inputValue, selectedMonthInfo, isSelectedMonthInThePast],
  );

  const handleInputValueChange = (value: number) => {
    setInputValue(value);
  };

  const handleSave = () => {
    const [month, year] = selectedLimitMonth.split('-');
    onSave(year, month, inputValue);
  };

  const clearChanges = () => {
    setInputValue(selectedMonthInfo.currentLimit);
  };

  const renderSelectValue = (value) =>
    monthOptions.find((opt) => opt.value === value)?.text ?? '';

  const renderLeaseDetailsContent = () => {
    const { leaseDetails } = selectedMonthInfo;

    if (!leaseDetails) {
      return (
        <Box p={2}>
          <Typography variant="titleSmall">
            {formatMessage(messages.noLeaseDetails)}
          </Typography>
        </Box>
      );
    }

    const scheduledNames =
      leaseDetails.scheduledLeases?.map((lease) => lease.headOfHousehold) || [];
    const currentNames =
      leaseDetails.currentLeases?.map((lease) => lease.headOfHousehold) || [];
    const priorNames =
      leaseDetails.priorLeases?.map((lease) => lease.headOfHousehold) || [];

    const renderLeaseTypeRow = (icon, label, names, isLast = false) => {
      if (names.length === 0) return null;

      return (
        <Box key={`${label}-${names.length}`}>
          <Box display="flex" alignItems="flex-start" px={2} py={1.5}>
            {/* Left column - Chip with icon and label */}
            <Box mr={2} width={120} display="flex" justifyContent="flex-end">
              <Chip
                icon={icon}
                label={label}
                variant="outlined"
                color="info"
                size="small"
              />
            </Box>

            {/* Right column - Resident names */}
            <Box flex={1} width={150} display="flex">
              <Stack spacing={0.5}>
                {names.map((name, index) => {
                  return (
                    <Typography key={index} variant="body">
                      {name}
                    </Typography>
                  );
                })}
              </Stack>
            </Box>
          </Box>
          {!isLast && <Divider />}
        </Box>
      );
    };

    const allSections = [
      { type: 'scheduled', icon: <CalendarIcon />, names: scheduledNames },
      { type: 'current', icon: <AssignmentIcon />, names: currentNames },
      { type: 'prior', icon: <ArrowBackIcon />, names: priorNames },
    ].filter((section) => section.names.length > 0);

    if (allSections.length === 0) {
      return (
        <Box p={2}>
          <Typography variant="body">
            {formatMessage(messages.noLeaseDetails)}
          </Typography>
        </Box>
      );
    }

    const labels = {
      scheduled: formatMessage(messages.scheduledLeases),
      current: formatMessage(messages.currentLeases),
      prior: formatMessage(messages.priorLeases),
    };

    return (
      <Paper elevation={0}>
        <Stack spacing={0}>
          {allSections.map((section, index) => {
            const isLast = index === allSections.length - 1;

            return renderLeaseTypeRow(
              section.icon,
              labels[section.type],
              section.names,
              isLast,
            );
          })}
        </Stack>
      </Paper>
    );
  };

  useEffect(() => {
    setInputValue(selectedMonthInfo.currentLimit);
  }, [selectedMonthInfo.currentLimit]);

  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <Select
        name="selectedLimitMonth"
        options={monthOptions}
        value={selectedLimitMonth}
        onChange={onSelectedLimitMonthChange}
        sx={{ width: 150 }}
        renderValue={renderSelectValue}
      />
      <Spacer h={10} />
      <b>{formatMessage(messages.currentLimit)}: </b>
      {canEdit ? (
        <>
          <Spacer h={10} />
          <LimitInput
            value={inputValue}
            onChange={handleInputValueChange}
            style={{ width: 91, height: 36 }}
            disabled={isSaving || isSelectedMonthInThePast}
          />
          <Spacer h={5} />
          <IconLink
            iconClass="et-isolated-check"
            color="#4CBB17"
            iconFontSize={20}
            onClick={handleSave}
            disabled={areButtonsDisabled}
          />
          <IconLink
            iconClass="et-delete"
            color="#D70000"
            iconFontSize={12}
            onClick={clearChanges}
            disabled={areButtonsDisabled}
          />
        </>
      ) : (
        <>
          <Spacer h={2} />
          <b>{selectedMonthInfo.currentLimit.toFixed(1)}%</b>
        </>
      )}
      <Spacer h={10} />
      <b>{formatMessage(messages.leasesPlural)}: </b>
      <Spacer h={3} />
      <span>{selectedMonthInfo.leaseLimit}</span>
      <Spacer h={20} />
      <span style={{ color: '#9A9A9A' }}>/</span>
      <Spacer h={20} />
      <b>{formatMessage(messages.actuals)}: </b>
      <Spacer h={3} />
      <span>{selectedMonthInfo.actualLimit.toFixed(1)}%</span>
      <Spacer h={10} />
      <b>{formatMessage(messages.leasesPlural)}: </b>
      <Spacer h={3} />
      <span>{selectedMonthInfo.leaseCount}</span>
      <Spacer h={10} />
      <IconButton onClick={() => setIsModalOpen(true)} size="small">
        <AlertInfoIcon />
      </IconButton>

      <Modal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={`Lease Details - ${renderSelectValue(selectedLimitMonth)}`}
        hideActions={true}
        ModalProps={{
          maxWidth: 'md',
        }}
        ContentProps={{
          sx: {
            maxHeight: '50vh',
            overflow: 'auto',
            p: 0,
          },
        }}
      >
        {renderLeaseDetailsContent()}
      </Modal>
    </div>
  );
}

export default SelectedLimit;
